import React, { useState } from 'react';
import { Bot, ExternalLink, Loader2, Search, Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { adminAPI } from '../services/api';
import { SocialPreview } from './SocialPreview';

const LANGS = ['fr', 'en', 'de', 'es', 'it'];
const KINDS = { home: "Page d'accueil", ranking: 'Classement budget entretien', vehicle_brand: 'Marque de véhicule', vehicle_model: 'Modèle de véhicule', product: 'Fiche produit', category: 'Catégorie', subcategory: 'Sous-catégorie', brand: 'Marque', article: 'Guide / article' };
const BOTS = 'googlebot|google-inspectiontool|storebot-google|bingbot|yandex|baiduspider|duckduckbot|slurp|applebot|facebookexternalhit|twitterbot|linkedinbot|whatsapp|telegrambot|discordbot|pinterest|slackbot|embedly';
const PATHS = '(produit|pieces|marque|article|vehicules)';
const SINGLE = 'budget-entretien';

const WORKER = `// Cloudflare Worker — routes : www.otopieces.com/ (accueil), /produit/*, /pieces/*, /marque/*, /article/*, /vehicules/*, /budget-entretien, /favicon.ico
const BOTS = /${BOTS}/i;
const PATHS = /^\\/(${PATHS}\\/[^?]+|${SINGLE})?$/;
export default {
  async fetch(request) {
    const url = new URL(request.url);
    const ua = request.headers.get('user-agent') || '';
    if (url.pathname === '/favicon.ico') {
      // favicon dynamique (toujours à jour avec l'icône choisie dans l'admin) pour Google, Bing et les navigateurs
      const target = new URL(url); target.pathname = '/api/branding/favicon.ico';
      return fetch(target.toString(), { headers: request.headers });
    }
    if (request.method === 'GET' && PATHS.test(url.pathname) && BOTS.test(ua)) {
      const target = new URL(url);
      target.pathname = '/api/prerender' + url.pathname.replace(/\\/$/, '');
      if (url.pathname === '/') target.pathname = '/api/prerender/';
      const res = await fetch(target.toString(), { headers: { 'user-agent': ua } });
      const headers = new Headers(res.headers);
      headers.set('Vary', 'User-Agent');
      return new Response(res.body, { status: res.status, headers });
    }
    return fetch(request);
  },
};`;

const NGINX = `# nginx — à placer dans le bloc server de www.otopieces.com
map $http_user_agent $seo_bot {
  default 0;
  ~*(${BOTS}) 1;
}
location = / {
  if ($seo_bot) { rewrite ^ /api/prerender/ last; }
  try_files /index.html =404;
}
location = /favicon.ico { rewrite ^ /api/branding/favicon.ico last; }
location ~ ^/(${PATHS}/(?<rest>.+?)|${SINGLE})/?$ {
  if ($seo_bot) { rewrite ^/(.*?)/?$ /api/prerender/$1 last; }
  try_files $uri /index.html;
}
location /api/ { proxy_pass http://127.0.0.1:8001; proxy_set_header Host $host; proxy_set_header X-Forwarded-Proto $scheme; }`;

const Snippet = ({ id, title, code }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(code).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }).catch(() => {}); };
  return (
    <details className="border rounded-lg p-3" data-testid={`prerender-snippet-${id}`}>
      <summary className="text-sm font-medium text-gray-800 cursor-pointer">{title}</summary>
      <div className="relative mt-2">
        <button type="button" onClick={copy} className="absolute right-2 top-2 text-xs inline-flex items-center gap-1 bg-white border rounded px-2 py-0.5 text-gray-600 hover:text-gray-900" data-testid={`prerender-copy-${id}`}>{copied ? <CheckCircle2 size={12} className="text-green-600" /> : <Copy size={12} />} {copied ? 'Copié' : 'Copier'}</button>
        <pre className="bg-gray-900 text-gray-100 text-[11px] rounded-md p-3 overflow-x-auto whitespace-pre">{code}</pre>
      </div>
    </details>
  );
};

const Result = ({ result }) => (
  <div className="border rounded-lg p-3 mb-4 bg-gray-50 text-sm space-y-1" data-testid="prerender-result">
    <p className="flex items-center gap-2 font-medium text-gray-900"><CheckCircle2 size={15} className="text-green-600" /> HTTP {result.status} · {KINDS[result.kind] || result.kind} · {result.name}</p>
    <p><span className="text-gray-500">Titre :</span> <span data-testid="prerender-result-title">{result.title}</span></p>
    <p><span className="text-gray-500">Description :</span> <span data-testid="prerender-result-description">{result.description}</span></p>
    <p><span className="text-gray-500">Canonical :</span> <code className="text-xs">{result.canonical}</code></p>
    <p className="text-xs text-gray-500">
      {result.kind === 'product' ? `${result.images} image(s) · ${result.specs} caractéristique(s) · ${result.reviews} avis vérifié(s)${result.reviews_with_photos ? ` (${result.reviews_with_photos} avec photo)` : ''} · ` : result.count != null ? `${result.count} produit(s) listé(s) · ` : ''}{(result.bytes / 1024).toFixed(1)} Ko
    </p>
    {result.image && <img src={result.image} alt="" className="h-20 rounded border bg-white" data-testid="prerender-result-image" />}
    <div className="flex flex-wrap gap-2 pt-1">
      <a href={result.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-orange-600 hover:underline" data-testid="prerender-open">Voir comme Googlebot <ExternalLink size={12} /></a>
      <a href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(result.canonical || result.url)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-md bg-[#1a73e8] hover:bg-[#1765c9] text-white px-3 py-1.5 text-xs font-medium" data-testid="prerender-rich-results-page" title="Teste la page publique telle que Google la voit (données structurées : prix, stock, avis, caractéristiques)">Tester les résultats enrichis Google <ExternalLink size={12} /></a>
      <a href={`https://search.google.com/test/rich-results?url=${encodeURIComponent(result.url)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gray-600 hover:underline" data-testid="prerender-rich-results">Tester la version prerender <ExternalLink size={12} /></a>
      <a href={`https://validator.schema.org/#url=${encodeURIComponent(result.canonical || result.url)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-gray-600 hover:underline" data-testid="prerender-schema-validator">Validateur schema.org <ExternalLink size={12} /></a>
    </div>
    <SocialPreview social={result.social} />
  </div>
);

// Admin › Paramètres : preview of the server-rendered HTML served to search bots (products, categories, brands, articles) + routing snippets
export const PrerenderPanel = () => {
  const [query, setQuery] = useState('');
  const [lang, setLang] = useState('fr');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const check = async (e) => {
    e?.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    try { setResult(await adminAPI.prerenderCheck(q.startsWith('/') || q.startsWith('http') ? { path: q.replace(/^https?:\/\/[^/]+/, '') } : { product: q }, lang)); }
    catch (err) { setResult(null); toast.error(err.response?.data?.detail || 'Vérification impossible'); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6 max-w-3xl mt-6" data-testid="prerender-panel">
      <div className="flex items-center gap-2 mb-1"><Bot size={20} className="text-orange-500" /><h2 className="font-semibold text-gray-900">Pré-rendu SEO pour les robots</h2></div>
      <p className="text-sm text-gray-500 mb-4">Fiches produit, catégories, sous-catégories, marques et guides existent en version HTML complète générée par le serveur (<code className="bg-gray-100 px-1 rounded">/api/prerender/…</code>) : titre, méta description, image, canonical, hreflang 5 langues, Open Graph et JSON-LD déjà remplis, sans JavaScript. Les robots (Google, Bing, réseaux sociaux) y sont aiguillés depuis l'URL publique grâce à l'un des réglages ci-dessous.</p>

      <form onSubmit={check} className="flex flex-wrap items-center gap-2 mb-3">
        <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Réf. produit, slug, ou chemin : / (accueil), /pieces/freinage, /marque/bosch, /article/1" className="flex-1 min-w-[220px]" data-testid="prerender-product-input" />
        <select value={lang} onChange={(e) => setLang(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-sm bg-white" data-testid="prerender-lang">
          {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
        </select>
        <Button type="submit" disabled={loading || !query.trim()} className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white" data-testid="prerender-check">{loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Search size={14} className="mr-1" />} Vérifier</Button>
      </form>

      {result && <Result result={result} />}

      <div className="space-y-2">
        <Snippet id="cloudflare" title="Domaine sur Cloudflare : Worker à coller (recommandé, gratuit)" code={WORKER} />
        <Snippet id="nginx" title="Serveur nginx (VPS) : règle sur le User-Agent" code={NGINX} />
        <details className="border rounded-lg p-3" data-testid="prerender-snippet-emergent">
          <summary className="text-sm font-medium text-gray-800 cursor-pointer">Hébergement Emergent sans proxy</summary>
          <p className="text-xs text-gray-600 mt-2">Le trafic hors <code>/api</code> est servi en statique : impossible d'y reconnaître un robot. Google exécute le JavaScript des pages (titre, méta et JSON-LD sont posés au chargement) mais l'indexation peut prendre plus de temps. Vous pouvez tout de même soumettre l'URL pré-rendue à l'outil « Test des résultats enrichis » pour contrôler les données, et activer un Worker Cloudflare dès que le DNS du domaine y est délégué.</p>
        </details>
      </div>
    </div>
  );
};

export default PrerenderPanel;
